/**
 * TaskCard component for the Indigo design system.
 *
 * A card for a reviewable item: a leading icon tile, an optional "completed"
 * check in the top-right, a title, and a status slot. Used for grids like the
 * "Review before appointment" card layout.
 *
 * With `adaptive`, the card collapses to a compact checklist row (title +
 * completion indicator, icon/status hidden) once its own width gets small —
 * driven by a CSS container query, so it adapts to the space it's given.
 *
 * @example
 * ```tsx
 * <TaskCard
 *   iconName="pdf"
 *   title="AA cover letter"
 *   completed
 *   status={<StatusChip mode="dot" dotVariant="success" label="Reviewed" />}
 *   onClick={openDoc}
 * />
 * ```
 */
import { clsx } from "clsx";
import {
  forwardRef,
  type KeyboardEvent,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { IconBadge } from "../IconBadge";
import { Icon, type IconProps } from "../Icons";
import { Heading } from "../Typography/Heading";
import "./TaskCard.css";

export interface TaskCardProps {
  /** Name of the icon shown in the leading tile. */
  iconName: IconProps["name"];

  /** Card title. */
  title: ReactNode;

  /** Status slot rendered below the title — typically a `StatusChip`. */
  status?: ReactNode;

  /** When true, shows the completed check (top-right, and in the compact row). */
  completed?: boolean;

  /**
   * When true, the card collapses to a compact checklist row at narrow widths
   * (container-query driven).
   * @default false
   */
  adaptive?: boolean;

  /** Click handler. When set, the card becomes an interactive button. */
  onClick?: MouseEventHandler<HTMLDivElement>;

  /** Additional CSS classes applied to the card. */
  className?: string;
}

const CompletedCheck = ({ size = "l" }: { size?: "m" | "l" }) => (
  <Icon name="functional-check-green" size={size} />
);

export const TaskCard = forwardRef<HTMLDivElement, TaskCardProps>(
  (
    {
      iconName,
      title,
      status,
      completed = false,
      adaptive = false,
      onClick,
      className,
    },
    ref,
  ) => {
    const interactive = Boolean(onClick);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (interactive && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onClick?.(event as unknown as Parameters<typeof onClick>[0]);
      }
    };

    return (
      <div
        className={clsx(
          "indigo-task-card-wrapper",
          adaptive && "indigo-task-card-wrapper--adaptive",
        )}
      >
        <div
          ref={ref}
          className={clsx(
            "indigo-task-card",
            interactive && "indigo-task-card--interactive",
            adaptive && "indigo-task-card--adaptive",
            className,
          )}
          role={interactive ? "button" : undefined}
          tabIndex={interactive ? 0 : undefined}
          onClick={onClick}
          onKeyDown={interactive ? handleKeyDown : undefined}
        >
          <div className="indigo-task-card__header">
            <IconBadge iconName={iconName} color="subtle" size={40} />
            {completed && (
              <span className="indigo-task-card__check" aria-label="Completed">
                <CompletedCheck size="m" />
              </span>
            )}
          </div>
          <Heading
            level={3}
            className="indigo-task-card__title text-indigo-text-secondary"
          >
            {title}
          </Heading>
          {status && <div className="indigo-task-card__status">{status}</div>}
          {/* Compact-only completion indicator (shown when adaptive + narrow). */}
          <span
            className="indigo-task-card__indicator"
            aria-label={completed ? "Complete" : "Incomplete"}
          >
            {completed ? (
              <CompletedCheck />
            ) : (
              <span className="indigo-task-card__incomplete">
                <span className="indigo-task-card__dash" aria-hidden="true" />
              </span>
            )}
          </span>
        </div>
      </div>
    );
  },
);

TaskCard.displayName = "TaskCard";
